import React from 'react'
import {
  Box,
  BoxProps,
  AlertTitle,
  AlertDescription,
  Stack,
  OrderedList,
  ListItem,
  Button,
  Tooltip,
  forwardRef,
  Text
} from '../../chakra'
import { AlertBanner } from '../alerts/Alerts'
import { Msg, useMsg } from '../../../common/localizationUtils'
import { CaretDown, CaretUp, Info } from '../../../icons'
import { useFormAlertListContext } from './FormAlertListContext'

export type FieldAlert = {
  id: string
  label?: string
  type: 'error' | 'warning'
  message: string
  link?: boolean
}

type AlertBannerListProps = {
  alerts?: Array<FieldAlert>
} & BoxProps

/**
 * FormAlertList
 *
 * FormAlertList are used to communicate a list of alerts that affects a system, feature or page. These include
 * errors and warnings. An array of FieldAlert objects is passed to the component to display the list of alerts.
 * Each alert is clickable and scrolls to the field that caused it, unless `link` is false.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-form-alert-list--docs
 */
export const FormAlertList = forwardRef(({ alerts = [], children, ...props }: AlertBannerListProps, ref) => {
  const [isWarningsCleared, setIsWarningsCleared] = React.useState(false)
  const ctx = useFormAlertListContext()

  // Get all error alerts from the context and passed in
  const specialErrors = alerts?.filter(alert => alert.type === 'error')
  const registeredErrors = ctx?.alerts?.filter(alert => alert.type === 'error') || []
  const errors = [...registeredErrors, ...specialErrors]

  // Get all warning alerts from the context and passed in
  const specialWarnings = alerts?.filter(alert => alert.type === 'warning')
  const registeredWarnings = ctx?.alerts?.filter(alert => alert.type === 'warning') || []
  const warnings = [...registeredWarnings, ...specialWarnings]

  const scrollToField = (error: FieldAlert) => {
    if (!ctx) return
    const node = ctx.targets[error.id]?.el as HTMLElement
    node?.scrollIntoView({ behavior: 'smooth' })

    // Focus any form field when scrolled to it.
    document.getElementById(`${error.id}`)?.focus()
  }

  if (errors.length === 0 && (warnings.length === 0 || isWarningsCleared)) return null

  return (
    <>
      <Box
        border="1px solid var(--vndly-color-input-border-invalid)"
        borderRadius="base"
        py={4}
        data-testid="form-alert-list"
        ref={ref}
        {...props}>
        <Stack spacing={6} flex="1">
          {errors.length > 0 && <FormErrorsList errors={errors} onClickScroll={scrollToField} />}
          {warnings.length > 0 && !isWarningsCleared && (
            <FormWarningsList
              warnings={warnings}
              onClickScroll={scrollToField}
              setIsWarningsCleared={setIsWarningsCleared}
            />
          )}
        </Stack>
      </Box>
    </>
  )
})

FormAlertList.displayName = 'FormAlertList'

function alertDisplayText(alert: FieldAlert) {
  return alert.label ? `${alert.label} - ${alert.message}` : alert.message
}

const AlertListItemContent = ({
  alert,
  onClickScroll
}: {
  alert: FieldAlert
  onClickScroll: (alert: FieldAlert) => void
}) => {
  const text = alertDisplayText(alert)
  if (alert.link === false) {
    return (
      <Text as="span" px={4} textStyle="bodySemi">
        {text}
      </Text>
    )
  }
  return (
    <Button variant="link" textDecoration="underline" height={8} onClick={() => onClickScroll(alert)}>
      {text}
    </Button>
  )
}

type FormErrorsListProps = {
  errors: Array<FieldAlert>
  onClickScroll: (error: FieldAlert) => void
}

const FormErrorsList = ({ errors, onClickScroll }: FormErrorsListProps) => {
  const [showMoreErrors, setShowMoreErrors] = React.useState(false)
  return (
    <AlertBanner status="error" variant="list-error" alignItems="flex-start">
      <AlertTitle pb={4} tabIndex={0}>
        <Msg id="term.errors_found" count={errors.length} description="Number of errors found" />
      </AlertTitle>
      <AlertDescription>
        <OrderedList>
          {errors.map((error: FieldAlert, index: number) => (
            <ListItem key={`alert-error-${error.id}`} display={index < 3 || showMoreErrors ? undefined : 'none'}>
              <AlertListItemContent alert={error} onClickScroll={onClickScroll} />
            </ListItem>
          ))}
        </OrderedList>
        {errors.length > 3 && (
          <Button variant="viewAll" onClick={() => setShowMoreErrors(!showMoreErrors)} data-testid="toggle-more-errors">
            {showMoreErrors ? (
              <>
                <CaretUp /> <Msg id="common.ui.show_less" />
              </>
            ) : (
              <>
                <CaretDown /> <Msg id="common.ui.show_more" />
              </>
            )}
          </Button>
        )}
      </AlertDescription>
    </AlertBanner>
  )
}

type FormWarningsListProps = {
  warnings: Array<FieldAlert>
  onClickScroll: (error: FieldAlert) => void
  setIsWarningsCleared: React.Dispatch<React.SetStateAction<boolean>>
}

const FormWarningsList = ({ warnings, onClickScroll, setIsWarningsCleared }: FormWarningsListProps) => {
  const [showMoreWarnings, setShowMoreWarnings] = React.useState(false)
  const msg = useMsg()
  return (
    <AlertBanner status="warning" variant="list-warning" alignItems="flex-start">
      <AlertTitle pb={4} tabIndex={0}>
        <Stack isInline>
          <Msg id="term.warnings_found" count={warnings.length} description="Number of warnings found" />
          <Tooltip label={msg('form_alert_list.warnings_do_not_stop_you')} placement="top-start">
            <Info role="img" ml={1} tabIndex={0} />
          </Tooltip>
        </Stack>
      </AlertTitle>
      <AlertDescription>
        <OrderedList>
          {warnings.map((warning, index) => (
            <ListItem key={`alert-warning-${warning.id}`} display={index < 3 || showMoreWarnings ? undefined : 'none'}>
              <AlertListItemContent alert={warning} onClickScroll={onClickScroll} />
            </ListItem>
          ))}
        </OrderedList>
        <Button data-testid="clear-warnings" size="sm" mt={4} onClick={() => setIsWarningsCleared(true)}>
          <Msg id="term.clear_warnings" />
        </Button>
        {warnings.length > 3 && (
          <Button
            variant="viewAll"
            onClick={() => {
              setShowMoreWarnings(!showMoreWarnings)
            }}
            data-testid="toggle-more-warnings">
            {showMoreWarnings ? (
              <>
                <CaretUp /> <Msg id="common.ui.show_less" />
              </>
            ) : (
              <>
                <CaretDown /> <Msg id="common.ui.show_more" />
              </>
            )}
          </Button>
        )}
      </AlertDescription>
    </AlertBanner>
  )
}
